import {
  ADMIN_DELIVERIES_DETAILS,
  ChatBackIcon,
  ConversationListItemResponse,
  Div,
  lightTheme,
  P,
  renderFleetName,
  VDriveProvider,
  vDriveStatuses,
} from '@vgrubs/components';
import { HeaderActionSlot } from './Chat.styled';
import {
  Actions,
  ContextLine,
  HolderChip,
  LateChip,
  StatusDot,
  TitleBlock,
} from './PublicThreadHeader.styled';

type Props = {
  thread: ConversationListItemResponse;
  onBack: () => void;
};

/** What the thread is about, under the name: the delivery. */
const DeliveryLine = ({
  thread,
}: {
  thread: ConversationListItemResponse;
}) => {
  const { dispatch } = thread;
  if (!dispatch) return null;

  const status =
    vDriveStatuses[dispatch.deliveryStatus as keyof typeof vDriveStatuses];

  return (
    <ContextLine>
      {/* Most urgent first: on a phone the line is clipped at the end. */}
      {dispatch.late && <LateChip>Late</LateChip>}
      {status && <StatusDot $color={status.color}>{status.name}</StatusDot>}
      {/* A new tab, like the order link in a support thread: the
          conversation stays where it is. */}
      <a
        href={ADMIN_DELIVERIES_DETAILS.replace(
          ':id',
          String(dispatch.deliveryId)
        )}
        target="_blank"
        rel="noreferrer"
      >
        {dispatch.orderId ? `Order #${dispatch.orderId}` : 'Delivery'}
      </a>
      {dispatch.brandName && <span>{dispatch.brandName}</span>}
      {dispatch.provider && (
        <span>{renderFleetName(dispatch.provider as VDriveProvider)}</span>
      )}
    </ContextLine>
  );
};

/**
 * The bar over a dispatcher thread: the customer and their delivery. There
 * is no state to move: the AI answers until somebody writes to the customer
 * (or it hands the thread over) and then stays out for good, so who has the
 * customer is never a decision here.
 * The amber chip says the AI handed it over and nobody has answered yet.
 */
export const PublicThreadHeader = ({ thread, onBack }: Props) => (
  <>
    <Div $flex $alignCenter $gap="12px" style={{ minWidth: 0 }}>
      <Div $mobile>
        <HeaderActionSlot
          role="button"
          aria-label="Back to conversations"
          onClick={onBack}
        >
          <ChatBackIcon />
        </HeaderActionSlot>
      </Div>
      <TitleBlock>
        <P $m="0" $bold $color={lightTheme.darkGray}>
          {thread.name}
        </P>
        <DeliveryLine thread={thread} />
      </TitleBlock>
    </Div>

    <Actions>
      {thread.unansweredSince && (
        <HolderChip $ai={false} $waiting>
          Needs a person
        </HolderChip>
      )}
    </Actions>
  </>
);
